import React, { useEffect, useRef, useCallback } from 'react';
import { Rnd } from 'react-rnd';
import { gsap } from 'gsap';

const DesktopWindow = ({
  id,
  title,
  children,
  defaultWidth = 700,
  defaultHeight = 500,
  isOpen,
  isMinimized,
  isMaximized,
  zIndex,
  onFocus,
  onClose,
  onMinimize,
  onMaximize,
  dockIconRef,
  isTablet,
}) => {
  const windowRef = useRef(null);
  const rndRef = useRef(null);
  const prevPosRef = useRef(null);

  const getDefaultPos = useCallback(() => {
    const x = Math.max(40, (window.innerWidth - defaultWidth) / 2);
    const y = Math.max(40, (window.innerHeight - defaultHeight) / 2 - 30);
    return { x, y };
  }, [defaultWidth, defaultHeight]);

  // Open animation
  useEffect(() => {
    if (isOpen && !isMinimized && windowRef.current) {
      gsap.fromTo(windowRef.current,
        { scale: 0.8, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.3, ease: 'back.out(1.4)' }
      );
    }
  }, [isOpen, isMinimized]);

  // Minimize animation
  useEffect(() => {
    if (!windowRef.current) return;
    if (isMinimized) {
      gsap.to(windowRef.current, {
        scale: 0.3,
        opacity: 0,
        duration: 0.25,
        ease: 'power2.in',
      });
    }
  }, [isMinimized]);

  // Maximize / restore
  useEffect(() => {
    if (!rndRef.current || isTablet) return;
    if (isMaximized) {
      const cur = rndRef.current.resizableElement?.current;
      if (cur) {
        prevPosRef.current = {
          x: parseInt(cur.style.left) || getDefaultPos().x,
          y: parseInt(cur.style.top) || getDefaultPos().y,
          w: parseInt(cur.style.width) || defaultWidth,
          h: parseInt(cur.style.height) || defaultHeight,
        };
      }
      rndRef.current.updatePosition({ x: 0, y: 0 });
      rndRef.current.updateSize({ width: window.innerWidth, height: window.innerHeight - 28 - 72 });
    } else if (prevPosRef.current) {
      const p = prevPosRef.current;
      rndRef.current.updatePosition({ x: p.x, y: p.y });
      rndRef.current.updateSize({ width: p.w, height: p.h });
    }
  }, [isMaximized, isTablet, defaultWidth, defaultHeight, getDefaultPos]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (windowRef.current) {
      gsap.to(windowRef.current, {
        scale: 0.8,
        opacity: 0,
        duration: 0.2,
        ease: 'power2.in',
        onComplete: onClose,
      });
    } else {
      onClose();
    }
  };

  // Tablet: fixed fullscreen window
  if (isTablet) {
    return (
      <div
        ref={windowRef}
        className="desktop-window"
        data-app-id={id}
        style={{
          position: 'fixed',
          top: 24,
          left: 24,
          right: 24,
          bottom: 24,
          width: 'calc(100vw - 48px)',
          height: 'calc(100vh - 48px)',
          borderRadius: 24,
          zIndex,
          display: isMinimized ? 'none' : 'flex',
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.4)',
        }}
        onClick={onFocus}
      >
        <div className="window-titlebar" style={{ height: 44, minHeight: 44 }}>
          <div className="window-traffic-lights">
            <button
              className="traffic-light traffic-close"
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); handleClose(); }}
              aria-label="Close"
            >×</button>
          </div>
          <span className="window-title" style={{ fontSize: 14 }}>{title}</span>
        </div>
        <div className={`window-body window-body--${id}`} data-app-id={id}>{children}</div>
      </div>
    );
  }

  // Desktop: draggable + resizable
  const pos = getDefaultPos();

  return (
    <Rnd
      ref={rndRef}
      default={{
        x: pos.x,
        y: pos.y,
        width: defaultWidth,
        height: defaultHeight,
      }}
      minWidth={320}
      minHeight={240}
      bounds="parent"
      dragHandleClassName="window-titlebar"
      style={{ zIndex, display: isMinimized ? 'none' : 'flex' }}
      enableResizing={!isMaximized}
      disableDragging={isMaximized}
      onMouseDown={onFocus}
    >
      <div
        ref={windowRef}
        className={`desktop-window${isMaximized ? ' maximized' : ''}`}
        data-app-id={id}
        style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <div className="window-titlebar">
          <div className="window-traffic-lights">
            <button
              className="traffic-light traffic-close"
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); handleClose(); }}
              aria-label="Close"
            >×</button>
            <button
              className="traffic-light traffic-minimize"
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onMinimize(); }}
              aria-label="Minimize"
            >−</button>
            <button
              className="traffic-light traffic-maximize"
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onMaximize(); }}
              aria-label="Maximize"
            >+</button>
          </div>
          <span className="window-title">{title}</span>
        </div>
        <div className={`window-body window-body--${id}`} data-app-id={id}>{children}</div>
      </div>
    </Rnd>
  );
};

export default DesktopWindow;
